import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import CastList from "./components/CastList";
import RelatedMovies from "./components/RelatedMovies";
import useDocumentTitle from "@/hooks/useDocumentTitle";

function toParagraphs(text: string) {
  const parts = text.split("。").map((s) => s.trim()).filter(Boolean);
  const chunks: string[] = [];
  for (let i = 0; i < parts.length; i += 2) {
    chunks.push(parts.slice(i, i + 2).join("。") + "。");
  }
  return chunks;
}

export default function Detail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);
  const [favorited, setFavorited] = useState(false);
  const [toast, setToast] = useState("");

  const title = movie ? `${movie.title} - 剧情介绍与演员信息` : "影片未找到 - 交换朋友的妻子";
  useDocumentTitle(title, movie ? movie.synopsis : undefined);

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g)))
      .slice(0, 4);
  }, [movie]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  if (!movie) {
    return (
      <div className="bg-background-950 pt-24 md:pt-28 pb-20 min-h-[70vh]">
        <div className="mx-auto max-w-site px-4 md:px-6 text-center py-20">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-900 text-4xl text-primary-400">
            <i className="ri-error-warning-line"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl md:text-3xl text-foreground-50">抱歉，没有找到这部影片</h1>
          <p className="mt-3 text-sm text-foreground-400">
            你访问的影片可能已被移除或链接有误，可以返回首页继续浏览。
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
            <Link
              to="/category"
              className="px-6 py-3 rounded-md border border-background-600 text-foreground-100 text-sm font-medium hover:border-accent-500 hover:text-accent-400 transition-colors"
            >
              浏览电影分类
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const paragraphs = toParagraphs(movie.plot);

  return (
    <div className="bg-background-950 pt-24 md:pt-28 pb-16 md:pb-20">
      <div className="mx-auto max-w-site px-4 md:px-6">
        {/* Breadcrumb */}
        <nav className="text-xs text-foreground-500 mb-6 flex flex-wrap items-center gap-2">
          <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/category" className="hover:text-accent-400 transition-colors">电影分类</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-300">{movie.title}</span>
        </nav>

        {/* Top: poster + info */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="w-full max-w-[300px] mx-auto lg:mx-0 shrink-0">
            <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-800 border border-background-800">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} 剧情介绍`}
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute top-3 right-3 px-2.5 py-1 rounded-md bg-background-950/85 text-accent-400 font-label text-sm">
                {movie.rating.toFixed(1)}
              </span>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setFavorited((v) => !v);
                  showToast(favorited ? "已取消收藏（演示，刷新后不保留）" : "已加入收藏（演示，刷新后不保留）");
                }}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  favorited
                    ? "bg-accent-500 text-background-950"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={() => showToast("链接已复制（演示功能）")}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md border border-background-600 text-foreground-100 text-sm font-medium hover:border-accent-500 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-forward-line"></i> 分享
              </button>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">{movie.title}</h1>
            <p className="mt-2 text-sm text-foreground-500 font-label tracking-wide">
              {movie.originalTitle}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-foreground-300">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line text-accent-400"></i> {movie.year}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-earth-line text-accent-400"></i> {movie.region}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line text-accent-400"></i> {movie.duration}分钟
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-movie-2-line text-accent-400"></i> {movie.director}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {movie.genres.map((g) => (
                <Link
                  key={g}
                  to={`/category?genre=${encodeURIComponent(g)}`}
                  className="px-3 py-1 rounded-full bg-secondary-900/60 text-secondary-200 text-xs hover:bg-primary-500 hover:text-background-50 transition-colors"
                >
                  {g}
                </Link>
              ))}
            </div>

            <div className="mt-6 rounded-lg bg-background-900/60 border border-background-800 p-5">
              <p className="text-xs font-label text-foreground-500 tracking-wide">主演</p>
              <p className="mt-2 text-sm text-foreground-200 leading-relaxed">
                {movie.cast.map((c) => c.name).join(" · ")}
              </p>
            </div>

            <p className="mt-6 text-sm md:text-base text-foreground-300 leading-relaxed">{movie.synopsis}</p>
          </div>
        </div>

        {/* Plot */}
        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-50 flex items-center gap-2 mb-5">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span> 剧情介绍
          </h2>
          <div className="space-y-4 text-sm md:text-base text-foreground-300 leading-loose max-w-4xl">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>

        {/* Cast */}
        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-50 flex items-center gap-2 mb-6">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span> 演员列表
          </h2>
          <CastList cast={movie.cast} />
        </section>

        {/* Related */}
        <section className="mt-12 md:mt-16">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-50 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span> 相关推荐
            </h2>
            <Link to="/recommendations" className="text-sm text-foreground-400 hover:text-accent-400 transition-colors flex items-center gap-1 whitespace-nowrap">
              更多推荐 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <RelatedMovies list={related} />
        </section>

        {/* Category link */}
        <section className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/category"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-background-900 border border-background-800 text-sm text-foreground-200 hover:border-primary-700 transition-colors"
          >
            <i className="ri-layout-grid-line text-accent-400"></i> 浏览全部电影分类
          </Link>
          <Link
            to="/recommendations"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-background-900 border border-background-800 text-sm text-foreground-200 hover:border-primary-700 transition-colors"
          >
            <i className="ri-compass-3-line text-accent-400"></i> 查看推荐专题
          </Link>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-background-800 border border-background-700 text-sm text-foreground-100 animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}